import type { ClientDemand } from 'types/buyer'
import { Fragment, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { useStore } from 'store'
import { clientPanelPaths } from 'paths'
import { formatDate, toCurrency, formatZipCode } from 'utils'
import copy from 'copy.json'
import { DownloadIcon, InfoIcon, PencilIcon, PlusIcon } from 'icons'

import Button from 'components/Button'
import ButtonLink from 'components/ButtonLink'
import Status from 'components/Status'
import Spinner from 'components/Spinner'
import DeleteButton from 'components/DeleteButton'
import ConfirmationWarnModal from 'modals/ConfirmationWarnModal'
import ConsumptionDetailsModal from 'modals/ConsumptionDetailsModal'
import UpdateProfileFileModal from 'modals/UpdateProfileFileModal'
import DetailsWrapper from 'components/layout/DetailsWrapper'
import Detail from 'components/layout/Detail'
import EditDemandDetailsModal from 'pages/buyer/EditDemandDetailsModal'

type Props = {
  demand: ClientDemand
  onOfferDataRefresh: () => Promise<void>
}

const form_texts = copy.common.form
const texts = copy.buyer.demand_details
const button = copy.common.button
const profile_file_text = copy.modal.profile_file

export default function DemandDetailsRow({ demand, onOfferDataRefresh, ...props }: Props) {
  const { api } = useStore()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(false)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [expandDetails, setExpandDetails] = useState(false)
  const [demandDetails, setDemandDetails] = useState<ClientDemand | null>(null)
  const [deleteDemandModalOpen, setDeleteDemandModalOpen] = useState(false)
  const [editDemandDetailsModalOpen, setEditDemandDetailsModalOpen] = useState(false)
  const [consumptionDetailsModalOpen, setConsumptionDetailsModalOpen] = useState(false)
  const [updateProfileFileModalOpen, setUpdateProfileFileModalOpen] = useState(false)

  const getClientDemandDetails = async (demandId: string) => {
    setLoading(true)

    const response = await api.getClientResourceDetails('demand', demandId)
    setDemandDetails(response)
    setLoading(false)
  }

  const handleDemandDetailsClick = (demandId: string) => {
    if (!expandDetails) {
      getClientDemandDetails(demandId)
    }

    setExpandDetails(current => !current)
  }

  const handleDownloadProfileFile = (demandId: string) => {
    api.getClientProfileFiles('demand', demandId)
  }

  const handleDemandDelete = async (demandId: string) => {
    setDeleteLoading(true)
    const success = await api.deleteClientResourceDetails('demand', demandId)

    if (!success) return false

    handleDemandDataRefresh(demandId)
    setDeleteDemandModalOpen(false)
    setDeleteLoading(false)
    return true
  }

  const handleUpdateProfileFile = async (demandId: string, profileFile: File | null) => {
    const formData = new FormData()
    if (profileFile) formData.append('profileFile', profileFile)
    const success = await api.putClientProfileFile(formData, 'demand', demandId)

    if (!success) return false

    handleDemandDataRefresh(demandId)
    return true
  }

  const handleDemandDataRefresh = (demandId: string) => {
    getClientDemandDetails(demandId)
    onOfferDataRefresh()
  }

  const handleGetMatch = (demandId: string) => {
    navigate(`${clientPanelPaths.matches}/${demandId}`)
  }

  return (
    <Fragment {...props}>
      <tr
        className={expandDetails ? 'expandedRow firstExpandedRow' : undefined}
        data-testid={demand.business_id}
      >
        <td>
          <Status statusType={demand.status} />
        </td>

        <td>
          <strong>{demand.osd_name}</strong>
        </td>

        <td>{demand.electric_energy_meter_number}</td>
        <td>
          {demand.yearly_power_consumption} {copy.common.MWh}
        </td>
        <td>{toCurrency(demand.current_energy_price)}</td>

        <td>
          <Button
            secondary={expandDetails}
            small
            style={{ minWidth: 85, marginLeft: 'auto' }}
            onClick={() => handleDemandDetailsClick(demand.business_id)}
          >
            {loading ? <Spinner size={16} /> : expandDetails ? button.roll : button.unroll}
          </Button>
        </td>
      </tr>

      {demandDetails && (
        <>
          <tr className={expandDetails ? 'expandedRow' : 'hiddenRow'}>
            <td colSpan={6}>
              <DetailsWrapper>
                <DetailsWrapper>
                  <Detail>
                    <span>{form_texts.demand_location}</span>
                    <span>{`${formatZipCode(demandDetails.location_zip_code)} ${
                      demandDetails.location_city
                    }`}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.current_energy_provider_name}</span>
                    <span>{demandDetails.current_energy_provider_name || '-'}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.energy_point_number}</span>
                    <span>{demandDetails.energy_point_number || '-'}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.energy_tariff_group}</span>
                    <span>{demandDetails.energy_tariff_group}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.energy_multiplier}</span>
                    <span>{`x${demandDetails.energy_multiplier}`}</span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.contract_length_demand}</span>
                    <span>
                      {demandDetails.contract_length} {copy.common.months_abbr}
                    </span>
                  </Detail>
                </DetailsWrapper>

                <DetailsWrapper>
                  <Detail>
                    <span>{form_texts.current_contract_end_date}</span>
                    <span>
                      {!demandDetails.current_contract_end_date
                        ? '-'
                        : formatDate(demandDetails.current_contract_end_date, {
                            options: {
                              day: 'numeric',
                              month: 'numeric',
                              year: 'numeric'
                            }
                          })}
                    </span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.contract_start_date_demand}</span>
                    <span>
                      {formatDate(demandDetails.contract_start_date, {
                        options: {
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric'
                        }
                      })}
                    </span>
                  </Detail>

                  <Detail>
                    <span>{form_texts.created_date}</span>
                    <span>{formatDate(demandDetails.created_date)}</span>
                  </Detail>
                </DetailsWrapper>
              </DetailsWrapper>

              <DetailsWrapper style={{ marginTop: 24 }}>
                <Detail>
                  <h3>{texts.subheading_profile_file}</h3>

                  <DownloadWrapper>
                    <Detail>
                      <span>{texts.original_profile_file}</span>
                      {demandDetails.has_original_profile_file ? (
                        <ProfileFileDownloadChangeWrapper>
                          <Button
                            secondary
                            small
                            disabled={!demandDetails.has_original_profile_file}
                            onClick={() => handleDownloadProfileFile(demandDetails.business_id)}
                          >
                            <DownloadIcon />
                            {button.download}
                          </Button>
                          {demandDetails.status === 'INACTIVE' && (
                            <ButtonLink onClick={() => setUpdateProfileFileModalOpen(true)}>
                              {profile_file_text.change_file}
                            </ButtonLink>
                          )}
                        </ProfileFileDownloadChangeWrapper>
                      ) : (
                        <Button
                          type="button"
                          small
                          onClick={() => setUpdateProfileFileModalOpen(true)}
                        >
                          <PlusIcon />
                          {button.add}
                        </Button>
                      )}
                    </Detail>

                    {demandDetails.has_original_profile_file &&
                      demandDetails.status === 'ACTIVE' && (
                        <Detail>
                          <button
                            className="green text"
                            onClick={() => setConsumptionDetailsModalOpen(true)}
                            style={{ marginLeft: -67 }}
                          >
                            {texts.button.consumption_details}
                          </button>
                        </Detail>
                      )}
                  </DownloadWrapper>
                </Detail>

                <Detail>
                  <h3>{texts.subheading_status}</h3>
                  <Statuses>
                    <Status statusType={demandDetails.status} />
                  </Statuses>
                </Detail>

                <ActionButtons>
                  <Detail style={{ marginLeft: 'auto' }}>
                    {demandDetails.status === 'INACTIVE' ? (
                      <Button
                        small
                        secondary
                        style={{ marginTop: 'auto', height: 38 }}
                        onClick={() => setEditDemandDetailsModalOpen(true)}
                      >
                        <PencilIcon />
                        {button.edit}
                      </Button>
                    ) : (
                      <OfferInfo>
                        <InfoIcon />
                        <span>{texts.cannot_edit_demand}</span>
                      </OfferInfo>
                    )}
                  </Detail>

                  {demandDetails.status !== 'DELETED' && demandDetails.status !== 'CLOSED' && (
                    <Detail style={{ marginLeft: 'auto' }}>
                      <DeleteButton
                        onClick={() => setDeleteDemandModalOpen(true)}
                        style={{ marginTop: 'auto' }}
                      >
                        {button.delete}
                      </DeleteButton>
                    </Detail>
                  )}
                </ActionButtons>
              </DetailsWrapper>

              {demandDetails.status === 'ACTIVE' && (
                <DetailsWrapper style={{ marginTop: 24 }}>
                  <Detail>
                    <h3>{texts.subheading_matching_offers}</h3>
                    <Button secondary onClick={() => handleGetMatch(demandDetails.business_id)}>
                      {button.look}
                    </Button>
                  </Detail>
                </DetailsWrapper>
              )}
            </td>
          </tr>

          {/* Delete demand  */}
          <ConfirmationWarnModal
            title={texts.modal.delete.title}
            description={texts.modal.delete.description}
            isModalOpen={deleteDemandModalOpen}
            onModalClose={() => setDeleteDemandModalOpen(false)}
            onModalConfirmation={() => handleDemandDelete(demandDetails.business_id)}
            confirmButtonLabel={button.delete}
            closeButtonLabel={button.close}
            loading={deleteLoading}
          />

          {/* Edit demand data */}
          <EditDemandDetailsModal
            demandData={demandDetails}
            isModalOpen={editDemandDetailsModalOpen}
            onModalClose={() => setEditDemandDetailsModalOpen(false)}
            refreshData={() => handleDemandDataRefresh(demandDetails.business_id)}
          />

          {/* Consumption details */}
          <ConsumptionDetailsModal
            demandId={demandDetails.business_id}
            isModalOpen={consumptionDetailsModalOpen}
            onModalClose={() => setConsumptionDetailsModalOpen(false)}
          />

          {/* Update profile file modal */}
          <UpdateProfileFileModal
            title={
              demandDetails.has_original_profile_file
                ? profile_file_text.confirm_change_profile_file
                : profile_file_text.confirm_add_profile_file
            }
            description={demandDetails.has_original_profile_file ? 'change_only' : ''}
            fieldErrorName="upsert_demand_original_profile_file.profile_file"
            isModalOpen={updateProfileFileModalOpen}
            onModalClose={() => setUpdateProfileFileModalOpen(false)}
            onModalConfirmation={(file: File | null) =>
              handleUpdateProfileFile(demandDetails.business_id, file)
            }
          />
        </>
      )}
    </Fragment>
  )
}

const DownloadWrapper = styled.div`
  display: flex;
  gap: 30px;
`

const Statuses = styled.div`
  display: flex;
  gap: 24px;
`

const ActionButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-left: auto;
`

const OfferInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: auto;
  font: var(--400-13);
  width: 100%;
  max-width: 275px;

  svg {
    flex: none;
  }
`

const ProfileFileDownloadChangeWrapper = styled.div`
  display: flex;
  justify-content: center;
`
